<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="modelValue" class="base-modal" @click="handleBackdropClick">
        <div class="base-modal__container" :class="`base-modal__container--${size}`">
          <div class="base-modal__content">
            <div class="base-modal__header">
              <h3 class="base-modal__title">{{ title }}</h3>
              <button
                class="base-modal__close"
                @click="close"
                aria-label="閉じる"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <div class="base-modal__body">
              <slot />
            </div>
            
            <div v-if="$slots.footer" class="base-modal__footer">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'BaseModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large', 'full'].includes(value)
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  watch: {
    modelValue(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
    },
    handleBackdropClick(event) {
      if (this.closeOnBackdrop && event.target === event.currentTarget) {
        this.close();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.base-modal {
  @include absolute-full;
  position: fixed;
  z-index: $z-index-modal;
  background-color: rgba($black, 0.5);
  backdrop-filter: blur(4px);
  @include flex-center;
  padding: $spacing-md;
  overflow-y: auto;
  
  &__container {
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    
    &--small {
      max-width: 400px;
    }
    
    &--medium {
      max-width: 600px;
    }
    
    &--large {
      max-width: 900px;
    }
    
    &--full {
      max-width: calc(100vw - #{$spacing-xl});
      max-height: calc(100vh - #{$spacing-xl});
    }
  }
  
  &__content {
    background-color: $white;
    border-radius: $radius-lg;
    box-shadow: $shadow-xl;
    display: flex;
    flex-direction: column;
    max-height: 100%;
  }
  
  &__header {
    @include flex-between;
    padding: $spacing-lg;
    border-bottom: 1px solid $gray-200;
  }
  
  &__title {
    font-size: $font-size-xl;
    font-weight: 600;
    color: $primary-color;
    margin: 0;
  }
  
  &__close {
    @include flex-center;
    width: 40px;
    height: 40px;
    background: none;
    border: none;
    border-radius: $radius-full;
    color: $gray-600;
    font-size: $font-size-lg;
    cursor: pointer;
    transition: all $transition-fast;
    
    &:hover {
      background-color: $gray-100;
      color: $gray-900;
    }
  }
  
  &__body {
    padding: $spacing-lg;
    overflow-y: auto;
    flex: 1;
  }
  
  &__footer {
    padding: $spacing-lg;
    border-top: 1px solid $gray-200;
  }
}

// Transition styles
.modal-enter-active,
.modal-leave-active {
  transition: opacity $transition-base;
  
  .base-modal__content {
    transition: transform $transition-base;
  }
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  
  .base-modal__content {
    transform: scale(0.9) translateY(20px);
  }
}</style>